import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FilterService } from '../../services/filter.service';
import { PlantingSiteDto } from '../../../../models/trofaco/plantingSiteDto';
import { FilterModel } from '../filter-checkbox-tree/filter-checkbox-tree.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { distinctUntilKeyChanged, map } from 'rxjs/operators';

@Component({
    selector: 'app-filter-list',
    templateUrl: './filter-list.component.html',
    styleUrls: ['./filter-list.component.css']
})
export class FilterListComponent implements OnInit {

    @Input()
    set plantingSites(sites: PlantingSiteDto[]) {
        sites = sites || [];
        this.countryFilters = FilterService.makeFilterModels(sites, value => value.country || '', this.countryFilters);
        this.yearFilters = FilterService.makeFilterModels(sites,
            value => new Date(value.plantingDate).getFullYear().toString(),
            this.yearFilters);
        this.yearFilters = this.yearFilters.sort((a, b) => { return b.name > a.name ? 1 : -1 })
        this.availableTreesFilters = FilterService.makeFilterModels(sites,
            value => this.availableTreesText(value),
            this.availableTreesFilters);
        this.queryParamsMap = {
            'country': this.countryFilters,
            'year': this.yearFilters,
            'trees': this.availableTreesFilters
        };
    }

    @Output() filterChange = new EventEmitter<(value: PlantingSiteDto) => boolean>(true);

    countryFilters: FilterModel<PlantingSiteDto>[] = [];
    dateFilters: FilterModel<PlantingSiteDto>[] = [];
    yearFilters: FilterModel<PlantingSiteDto>[] = [];
    availableTreesFilters: FilterModel<PlantingSiteDto>[] = [];

    queryParamsMap: { [key: string]: FilterModel<PlantingSiteDto>[]} = {};

    showYearPickerCalendar: boolean;

    constructor(private router: Router, private route: ActivatedRoute) {
    }

    ngOnInit() {
        this.route.queryParamMap
            .pipe(
                map(query => {
                    //used to get the values in the url to prevent loading values if they're not changed
                    let filterNames = Object.keys(this.queryParamsMap);
                    let values = filterNames.map(name => query.getAll(name).join(',')).join('|');
                    return ({query, values});
                }),
                distinctUntilKeyChanged('values')
            )
            .subscribe(result => {
                this.loadSelectedValues(result.query);
                this.filterUpdated();
            });

        this.route.data.subscribe(data => {
            this.showYearPickerCalendar = data.showYearPickerCalendar;
        });
    }

    loadSelectedValues(query: ParamMap) {
        let filterNames = Object.keys(this.queryParamsMap);
        for (let filterName of filterNames) {
            let filters = this.queryParamsMap[filterName];
            if (query.has(filterName)) {
                let values = query.getAll(filterName);
                this.updateFiltersSelected(values, filters);
            } else {
                //if empty then select all
                filters.forEach(f => f.selected = true);
            }
            this.queryParamsMap[filterName] = filters.slice();
        }
        this.countryFilters = this.queryParamsMap['country'];
        this.yearFilters = this.queryParamsMap['year'];
        this.availableTreesFilters = this.queryParamsMap['trees'];
    }

    updateFiltersSelected(selectedValues: string[], filters: FilterModel<PlantingSiteDto>[]) {
        let lowerCaseValues = selectedValues.map(value => value.toLowerCase());
        for (let filter of filters) {
            filter.selected = lowerCaseValues.includes(filter.name.toLowerCase())
        }
    }

    updateQueryParams() {
        let params = {...this.route.snapshot.queryParams};
        let filterNames = Object.keys(this.queryParamsMap);
        for (let filterName of filterNames) {
            delete params[filterName];
            let filters = this.queryParamsMap[filterName];
            if (this.allSelected(filters)) continue;
            params[filterName] = this.getSelectedNames(filters);
        }
        this.router.navigate([], {relativeTo: this.route, replaceUrl: true, queryParams: params});
    }

    allSelected(filters: FilterModel<PlantingSiteDto>[]) {
        return !filters.some(value => !value.selected);
    }

    getSelectedNames(filters: FilterModel<PlantingSiteDto>[]) {
        return filters.filter(value => value.selected).map(value => value.name);
    }

    availableTreesText(site: PlantingSiteDto) {
        return (site.availableTrees && site.availableTrees > 0) ? 'Available' : 'Fully sponsored';
    }

    filterUpdated() {
        this.updateQueryParams();
        let countryFilter = FilterService.combineFiltersWithOr(this.countryFilters);
        let yearFilter = FilterService.combineFiltersWithOr(this.yearFilters);
        let treeFilter = FilterService.combineFiltersWithOr(this.availableTreesFilters);
        let filter = (site: PlantingSiteDto) => {
            return countryFilter(site) && yearFilter(site) && treeFilter(site);
        };
        this.filterChange.emit(filter);
    }
}
